import {
  CatalogueMarketplace,
  HitItemCatalogue,
  ItemCatalogue2,
  ItemCatalogue2FFData,
  ItemCatalogue2MatferData,
} from 'api-types/ffconnect';
import {ItemCatalogue2Desc} from 'api-types/ffconnect-hal';
import {useHalState} from '@synako/haligator';
import {FollowPromiseOne, Navigable, State} from '@synako/halitrek';
import {
  AffichageLigneDeCommande,
  LigneDeCommandeAffichage,
  getAffichageLigneDeCommande,
} from '../../utils/commande-ligne-display';

// ItemCatalogue2 est un type plat : les données FF (FFData) et marketplace
// (MatferData) y sont fusionnées en intersection, donc leurs champs sont tous
// optionnels. Les types et guards ci-dessous réintroduisent, selon marketplace_id,
// la garantie de présence des champs propres à chaque variante (Required).
export type ItemCatalogue2FF = ItemCatalogue2 & {
  marketplace_id: 'FF';
} & Required<ItemCatalogue2FFData>;

export type ItemCatalogue2Marketplace = ItemCatalogue2 & {
  marketplace_id: Exclude<CatalogueMarketplace, 'FF'>;
} & Required<Pick<ItemCatalogue2MatferData, 'libelle' | 'matfer'>>;

/**
 * Type guard pour les articles France Frais.
 */
export const isItemCatalogueFF = (
  item: ItemCatalogue2,
): item is ItemCatalogue2FF => {
  return item.marketplace_id === 'FF';
};

/**
 * Type guard pour les articles marketplace (hors FF).
 */
export const isItemCatalogueMarketplace = (
  item: ItemCatalogue2,
): item is ItemCatalogue2Marketplace => {
  return item.marketplace_id !== 'FF';
};

export type ItemCatalogue2StateFF = State<ItemCatalogue2Desc> & {
  data: ItemCatalogue2FF;
};
export type ItemCatalogue2StateMarketplace = State<ItemCatalogue2Desc> & {
  data: ItemCatalogue2Marketplace;
};
type ItemCatalogue2HalState = ReturnType<
  typeof useHalState<ItemCatalogue2Desc>
>;
export type LigneDeCommandeMarketplaceItem = AffichageLigneDeCommande & {
  marketplaceItem: ItemCatalogue2HalState;
};

export const isItemStateCatalogueFF = (
  state: State<ItemCatalogue2Desc> | undefined,
): state is ItemCatalogue2StateFF =>
  state != null && isItemCatalogueFF(state.data);

export const isItemStateCatalogueMarketplace = (
  state: State<ItemCatalogue2Desc> | undefined,
): state is ItemCatalogue2StateMarketplace =>
  state != null && isItemCatalogueMarketplace(state.data);

export const useItemCatalogueOuMarketplace = (
  clientNav: Navigable,
  hit: HitItemCatalogue,
  client_id?: string,
) => {
  const nav: FollowPromiseOne<ItemCatalogue2Desc> = clientNav.follow(
    'go:item_catalogue_2',
    {
      marketplace_id: hit.marketplace_id,
      code: hit.code,
      client_id,
    },
  );

  return useHalState(nav);
};

// Récupère l'item catalogue marketplace correspondant à une ligne de commande
// quand celle-ci porte un marketplace_id.
export const useLigneDeCommandeMarketplaceItem = (
  clientNav: Navigable,
  ligne: LigneDeCommandeAffichage,
): LigneDeCommandeMarketplaceItem => {
  const ligneDisplay = getAffichageLigneDeCommande(ligne);
  const marketplaceNav = ligneDisplay.marketplaceId
    ? clientNav.follow('go:item_catalogue_2', {
        marketplace_id: ligneDisplay.marketplaceId,
        code: ligneDisplay.codeArticle,
      })
    : undefined;
  const marketplaceItem = useHalState(marketplaceNav);

  return {...ligneDisplay, marketplaceItem};
};
